import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, FileUp, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { zohoSyncPreview, zohoSyncConfirmar, getApiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/utils";

const GRUPOS = [
  { key: "nuevos", label: "Nuevos", tono: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "cambios", label: "Con cambios", tono: "text-blue-700 bg-blue-50 border-blue-200" },
  { key: "duplicados", label: "Posibles duplicados", tono: "text-amber-800 bg-amber-50 border-amber-200" },
  { key: "ausentes", label: "No están en el CSV", tono: "text-slate-700 bg-slate-50 border-slate-200" },
  { key: "sin_cambios", label: "Sin cambios", tono: "text-muted-foreground bg-muted/40 border-transparent" },
];

function EstadoPill({ estado }) {
  if (!estado) return null;
  return (
    <span className={cn("text-[11px] px-1.5 py-0.5 rounded", estado === "activo" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-600")}>
      {estado === "activo" ? "Activo" : "Inactivo"}
    </span>
  );
}

/**
 * Sincronización con Zoho a partir del CSV de contactos: subir → revisar diferencias → confirmar.
 * Nunca borra clientes ni pisa los datos propios del CRM (alias, logo, notas, chat…).
 */
export function ZohoSyncDialog({ open, onOpenChange, onDone }) {
  const inputRef = useRef(null);
  const [paso, setPaso] = useState("subir");
  const [cargando, setCargando] = useState(false);
  const [preview, setPreview] = useState(null);
  const [grupo, setGrupo] = useState("nuevos");
  const [duplicados, setDuplicados] = useState({});
  const [resultado, setResultado] = useState(null);
  const [arrastrando, setArrastrando] = useState(false);

  useEffect(() => {
    if (open) {
      setPaso("subir");
      setPreview(null);
      setResultado(null);
      setDuplicados({});
    }
  }, [open]);

  const procesar = async (file) => {
    if (!file) return;
    setCargando(true);
    try {
      const data = await zohoSyncPreview(file);
      setPreview(data);
      setGrupo(GRUPOS.find((g) => (data.contadores?.[g.key] || 0) > 0)?.key || "sin_cambios");
      setPaso("revisar");
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo leer el CSV"));
    } finally {
      setCargando(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const confirmar = async () => {
    setCargando(true);
    try {
      const data = await zohoSyncConfirmar(preview.preview_id, duplicados);
      setResultado(data);
      setPaso("hecho");
      onDone?.();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "No se pudo aplicar la sincronización"));
    } finally {
      setCargando(false);
    }
  };

  const c = preview?.contadores || {};
  const hayAlgoQueAplicar = (c.nuevos || 0) + (c.cambios || 0) + Object.keys(duplicados).length > 0;
  const items = preview?.[grupo] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90dvh] flex flex-col gap-4">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4" /> Sincronizar con Zoho
          </DialogTitle>
          <DialogDescription>
            Exporta los contactos desde Zoho Books en CSV y súbelo aquí. Verás los cambios antes de aplicarlos.
          </DialogDescription>
        </DialogHeader>

        {paso === "subir" && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setArrastrando(true);
            }}
            onDragLeave={() => setArrastrando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setArrastrando(false);
              procesar(e.dataTransfer.files?.[0]);
            }}
            disabled={cargando}
            className={cn(
              "flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed py-14 text-sm transition-colors",
              arrastrando ? "border-primary bg-primary/5" : "border-muted-foreground/25 hover:bg-muted/40"
            )}
          >
            {cargando ? <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /> : <FileUp className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />}
            <span className="font-medium">{cargando ? "Analizando…" : "Arrastra el CSV o haz clic para elegirlo"}</span>
            <span className="text-xs text-muted-foreground">Contactos.csv exportado de Zoho</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => procesar(e.target.files?.[0])} />

        {paso === "revisar" && preview && (
          <>
            <div className="flex flex-wrap gap-2">
              {GRUPOS.map((g) => (
                <button
                  key={g.key}
                  type="button"
                  onClick={() => setGrupo(g.key)}
                  className={cn(
                    "flex items-center gap-2 rounded-full border px-3 h-8 text-xs font-medium transition",
                    g.tono,
                    grupo === g.key ? "ring-2 ring-offset-1 ring-foreground/20" : "opacity-80 hover:opacity-100"
                  )}
                >
                  {g.label}
                  <span className="tabular-nums">{c[g.key] || 0}</span>
                </button>
              ))}
            </div>
            <div className="flex-1 min-h-[200px] overflow-y-auto rounded-lg border divide-y">
              {items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Nada en este grupo.</p>}
              {grupo === "nuevos" &&
                items.map((it) => (
                  <div key={it.zoho_id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                    <span className="flex-1 min-w-0 truncate">{it.nombre}</span>
                    <span className="text-xs text-muted-foreground">{it.nif}</span>
                    <EstadoPill estado={it.estado} />
                  </div>
                ))}
              {grupo === "cambios" &&
                items.map((it) => (
                  <div key={it.zoho_id} className="px-4 py-3 text-sm space-y-1.5">
                    <div className="flex items-center gap-2">
                      <span className="font-medium truncate">{it.nombre_visible}</span>
                      {it.vinculo === "nif" && <span className="text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700">Se vincula por NIF</span>}
                    </div>
                    {it.cambios.map((ch) => (
                      <div key={ch.campo} className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                        <span className="w-24 shrink-0">{ch.label}</span>
                        <span className="line-through decoration-muted-foreground/40">{ch.antes || "—"}</span>
                        <ArrowRight className="w-3 h-3 shrink-0" />
                        <span className="text-foreground">{ch.despues}</span>
                      </div>
                    ))}
                  </div>
                ))}
              {grupo === "duplicados" &&
                items.map((it) => (
                  <div key={it.zoho_id} className="px-4 py-3 text-sm space-y-2">
                    <p>
                      <span className="font-medium">{it.nombre}</span>
                      <span className="text-muted-foreground"> (Zoho) se parece a </span>
                      <span className="font-medium">{it.candidato.nombre_visible}</span>
                      <span className="text-muted-foreground"> (CRM)</span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { v: it.candidato.id, label: "Es el mismo: vincular" },
                        { v: "nuevo", label: "Son distintos: crear nuevo" },
                        { v: undefined, label: "Decidir luego" },
                      ].map((op) => (
                        <Button
                          key={op.label}
                          type="button"
                          size="sm"
                          variant={duplicados[it.zoho_id] === op.v ? "default" : "outline"}
                          className="h-8 text-xs"
                          onClick={() =>
                            setDuplicados((prev) => {
                              const next = { ...prev };
                              if (op.v === undefined) delete next[it.zoho_id];
                              else next[it.zoho_id] = op.v;
                              return next;
                            })
                          }
                        >
                          {op.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                ))}
              {grupo === "ausentes" && (
                <>
                  <p className="px-4 py-2.5 text-xs text-muted-foreground bg-muted/30">
                    Estos clientes vinieron de Zoho pero no aparecen en este CSV. No se borra nada; revisa si se eliminaron en Zoho.
                  </p>
                  {items.map((it) => (
                    <div key={it.cliente_id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                      <span className="flex-1 truncate">{it.nombre_visible}</span>
                      <EstadoPill estado={it.estado} />
                    </div>
                  ))}
                </>
              )}
              {grupo === "sin_cambios" &&
                items.map((it) => (
                  <div key={it.zoho_id} className="flex items-center gap-3 px-4 py-2 text-sm text-muted-foreground">
                    <span className="flex-1 truncate">{it.nombre_visible}</span>
                    <EstadoPill estado={it.estado} />
                  </div>
                ))}
            </div>
            {preview.avisos?.length > 0 && (
              <p className="text-xs text-amber-700">{preview.avisos.length} aviso(s): {preview.avisos.slice(0, 2).join(" · ")}</p>
            )}
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setPaso("subir")} disabled={cargando}>Subir otro CSV</Button>
              <Button onClick={confirmar} disabled={cargando}>
                {cargando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {hayAlgoQueAplicar ? "Aplicar cambios" : "Marcar como sincronizado"}
              </Button>
            </DialogFooter>
          </>
        )}

        {paso === "hecho" && resultado && (
          <div className="flex flex-col items-center gap-4 py-8 text-center">
            <CheckCircle2 className="w-10 h-10 text-emerald-500" strokeWidth={1.5} />
            <p className="font-medium">Sincronización completada</p>
            <div className="flex flex-wrap justify-center gap-2 text-xs">
              {[
                ["nuevos", "nuevos"],
                ["actualizados", "actualizados"],
                ["vinculados", "vinculados"],
                ["sin_cambios", "sin cambios"],
                ["omitidos", "pendientes de decidir"],
              ].map(([k, label]) =>
                resultado.contadores?.[k] ? (
                  <span key={k} className="rounded-full bg-muted px-3 py-1">
                    <strong className="tabular-nums">{resultado.contadores[k]}</strong> {label}
                  </span>
                ) : null
              )}
            </div>
            <Button onClick={() => onOpenChange(false)}>Cerrar</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
